/**
 * Checkpoint Client Types
 * 
 * Type definitions for the Checkpoint API client.
 * 
 * @package @appfactory/checkpoint-client
 * @version 1.0.0
 */

import type { CheckpointAppName } from './app-names';

/**
 * Configuration options for the Checkpoint client
 */
export interface CheckpointConfig {
  /** Base URL of the Checkpoint API server */
  baseUrl: string;
  
  /** SSO domain for authentication (e.g. https://members.appfactory.studio)
   * This property is now required; callers must provide it when creating the
   * client. A default is still documented but the type enforces explicit
   * configuration.
   */
  ssoDomain: string;
  /** optional OAuth2 client id for native/browser flows */
  clientId?: string;
  
  /** Storage mechanism for tokens ('localStorage' | 'sessionStorage' | 'memory') */
  storage?: StorageType;
  
  /** Custom storage implementation */
  customStorage?: TokenStorage;
  
  /** Callback when tokens are refreshed */
  onTokenRefresh?: (tokens: TokenPair) => void;
  
  /** Callback when authentication state changes */
  onAuthStateChange?: (authenticated: boolean, user?: User) => void;
  
  /** Auto-refresh tokens before expiry */
  autoRefresh?: boolean;
  
  /** Refresh tokens when less than this many seconds until expiry */
  refreshThreshold?: number;
  
  /** Request timeout in milliseconds */
  timeout?: number;
  
  /** Additional headers to include in requests */
  headers?: Record<string, string>;

  /** Parse App ID */
  appId?: string;

  /** Parse Server URL */
  serverURL?: string;

  /**
   * Application context for secondary-user auth flows.
   * Defaults to MEMBERS (members dashboard) when omitted.
   */
  appName?: CheckpointAppName;
}

/**
 * Storage type options
 */
export type StorageType = 'localStorage' | 'sessionStorage' | 'memory';

/**
 * Custom token storage interface
 */
export interface TokenStorage {
  getItem(key: string): string | null | Promise<string | null>;
  setItem(key: string, value: string): void | Promise<void>;
  removeItem(key: string): void | Promise<void>;
}

/**
 * JWT Token pair
 */
export interface TokenPair {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  refresh_expires_in: number;
}

/**
 * User object
 */
export interface User {
  id: number;
  email: string;
  username?: string;
  status: number;
  roles: string[];
}

/**
 * Authentication response
 */
export interface AuthResponse extends TokenPair {
  user: User;
  member?: Record<string, unknown> | null;
  requires_2fa?: boolean;
  two_factor_token?: string;
  used_recovery_code?: boolean;
  message?: string;
}

/**
 * SSO authentication response
 */
export interface SsoAuthResponse extends AuthResponse {
  /**
   * Metadata provided when logging in via SSO at the primary domain.
   * `ticket` will be filled in by the server when the user authenticates
   * during an SSO flow so the primary domain can redirect the user back
   * to the requesting site with a short-lived JWT.
   */
  sso: {
    target_domain: string;
    redirect_url?: string;
    session_id: string;
    ticket?: string;
  };

  /**
   * legacy/top-level copy of the ticket value, if present.
   * some consumers may look here for convenience.
   */
  sso_ticket?: string;
}

/**
 * Registration response
 */
export interface RegistrationResponse {
  user_id: number;
  email: string;
  username?: string;
  email_verification_required: boolean;
  message: string;
}

/**
 * Two-factor setup response
 */
export interface TwoFactorSetupResponse {
  secret: string;
  qr_code_uri: string;
  issuer: string;
  account_name: string;
  message: string;
}

/**
 * Two-factor enable response
 */
export interface TwoFactorEnableResponse {
  enabled: boolean;
  recovery_codes: string[];
  message: string;
}

/**
 * Two-factor status response
 */
export interface TwoFactorStatus {
  enabled: boolean;
  enabled_at?: string;
  recovery_codes: {
    total: number;
    remaining: number;
  };
}

/**
 * Authorization profile
 */
export interface AuthorizationProfile {
  user_id: number;
  tier: string;
  roles: string[];
  features: string[];
  traits: Record<string, unknown>;
}

/**
 * Permission check result
 */
export interface PermissionResult {
  allowed: boolean;
}

/**
 * Feature check result
 */
export interface FeatureResult {
  enabled: boolean;
}

/**
 * Allocation metric
 */
export interface AllocationMetric {
  limit: number;
  used: number;
  remaining: number;
  unlimited: boolean;
  percentage_used?: number;
}

/**
 * Resource allocation
 */
export interface ResourceAllocation {
  category: string;
  resource: string;
  tier: string;
  metrics: Record<string, AllocationMetric>;
}

/**
 * Full allocation status
 */
export interface AllocationStatus {
  [category: string]: {
    tier: string;
    base?: AllocationMetric;
    resources: {
      [resource: string]: {
        [metric: string]: AllocationMetric;
      };
    };
  };
}

/**
 * Allocation check result
 */
export interface AllocationCheckResult {
  available: boolean;
  remaining: number;
}

/**
 * Allocation consume result
 */
export interface AllocationConsumeResult {
  consumed: boolean;
  remaining: number;
}

/**
 * API response wrapper
 */
export interface ApiResponse<T = unknown> {
  success: boolean;
  data: T;
  error?: {
    code: string;
    message: string;
  };
  meta: {
    timestamp: number;
    version: string;
  };
}

/**
 * API error
 */
export class CheckpointError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode?: number
  ) {
    super(message);
    this.name = 'CheckpointError';
  }
}

/**
 * Login options
 */
export interface LoginOptions {
  /** Target audience/domain for the token */
  audience?: string;
  /** Remember login duration in seconds */
  rememberDuration?: number;
}

/**
 * Registration options
 */
export interface RegistrationOptions {
  /** Require email verification */
  requireVerification?: boolean;
}

/**
 * Permission check options
 */
export interface PermissionCheckOptions {
  /** Additional context for permission check */
  context?: Record<string, unknown>;
}

/**
 * Allocation consume options
 */
export interface AllocationConsumeOptions {
  /** Additional context for logging */
  context?: Record<string, unknown>;
}

/**
 * User profile
 */
export interface UserProfile {
  user_id: number;
  authorization: AuthorizationProfile;
  allocations: AllocationStatus;
}

/**
 * Verified token info
 */
export interface TokenInfo {
  user_id: number;
  user: User;
  member?: Record<string, unknown> | null;
  expires_at: number;
}
